/**
 * ABTestPanel — UI mínima de configuração de A/B test de assunto.
 *
 * - Lista 2-4 variantes (label + assunto + preheader opcional)
 * - Critério de vitória: open_rate ou click_rate
 * - Duração do teste em horas + % de amostra inicial
 * - Botões: "Salvar configuração" e "Escolher vencedor agora"
 */

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Trophy, Loader2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

type Criteria = "open_rate" | "click_rate";

type DraftVariant = {
  id?: number;
  label: string;
  subject: string;
  preheader: string;
};

export type ABTestPanelProps = {
  campaignId: number;
};

export function ABTestPanel({ campaignId }: ABTestPanelProps) {
  const [variants, setVariants] = useState<DraftVariant[]>([
    { label: "A", subject: "", preheader: "" },
    { label: "B", subject: "", preheader: "" },
  ]);
  const [criteria, setCriteria] = useState<Criteria>("open_rate");
  const [durationHours, setDurationHours] = useState(4);
  const [samplePercent, setSamplePercent] = useState(20);

  // Carrega variantes existentes
  const { data: existing, refetch } = trpc.emailCampaigns.listVariants.useQuery({ campaignId });

  useEffect(() => {
    if (!existing || existing.length === 0) return;
    setVariants(
      existing.map((v: any) => ({
        id: v.id,
        label: v.label,
        subject: v.subject,
        preheader: v.preheader ?? "",
      })),
    );
  }, [existing]);

  const setupMutation = trpc.emailCampaigns.setupABTest.useMutation({
    onSuccess: () => {
      toast.success("A/B test configurado.");
      refetch();
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const pickWinnerMutation = trpc.emailCampaigns.pickABTestWinner.useMutation({
    onSuccess: (winner) => {
      toast.success(`Vencedor: variante "${winner.label}".`);
      refetch();
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const addVariant = () => {
    if (variants.length >= 4) return;
    const nextLabel = String.fromCharCode(65 + variants.length); // A,B,C,D
    setVariants([...variants, { label: nextLabel, subject: "", preheader: "" }]);
  };

  const removeVariant = (idx: number) => {
    if (variants.length <= 2) return;
    setVariants(variants.filter((_, i) => i !== idx));
  };

  const updateVariant = (idx: number, patch: Partial<DraftVariant>) => {
    setVariants(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const canSave = variants.every((v) => v.label.trim() && v.subject.trim());
  const winnerVariantId = (existing as any)?.find?.((v: any) => v.isWinner)?.id;

  const handleSave = () => {
    if (!canSave) {
      toast.warning("Cada variante precisa de um label e um assunto.");
      return;
    }
    setupMutation.mutate({
      campaignId,
      winnerCriteria: criteria,
      testDurationHours: durationHours,
      testSamplePercent: samplePercent,
      variants: variants.map((v) => ({
        label: v.label.trim(),
        subject: v.subject.trim(),
        preheader: v.preheader.trim() || undefined,
      })),
    });
  };

  return (
    <Card className="m-3 p-4 border-amber-200 bg-amber-50/30">
      <div className="flex items-center gap-2 mb-3">
        <FlaskConical className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold">Teste A/B de Assunto</h3>
        {winnerVariantId && (
          <Badge variant="outline" className="ml-auto text-[10px] gap-1 border-emerald-300 bg-emerald-50 text-emerald-700">
            <Trophy className="w-3 h-3" /> Vencedor escolhido
          </Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Envia {samplePercent}% da lista (dividido entre as variantes), espera {durationHours}h, escolhe o vencedor por{" "}
        <span className="font-medium">{criteria === "open_rate" ? "taxa de abertura" : "taxa de clique"}</span>{" "}
        e dispara o vencedor para o restante.
      </p>

      <div className="space-y-2 mb-3">
        {variants.map((v, idx) => {
          const variantStats = (existing as any)?.[idx];
          const isWinner = variantStats?.isWinner;
          return (
            <div
              key={idx}
              className={`flex items-start gap-2 p-2 rounded-md border ${
                isWinner ? "border-emerald-400 bg-emerald-50/50" : "bg-background"
              }`}
            >
              <div className="flex flex-col gap-1 w-12 shrink-0">
                <Label className="text-[10px] text-muted-foreground">Label</Label>
                <Input
                  value={v.label}
                  onChange={(e) => updateVariant(idx, { label: e.target.value })}
                  className="h-7 text-xs"
                  maxLength={3}
                />
              </div>
              <div className="flex-1 min-w-0">
                <Label className="text-[10px] text-muted-foreground">Assunto</Label>
                <Input
                  value={v.subject}
                  onChange={(e) => updateVariant(idx, { subject: e.target.value })}
                  className="h-7 text-xs"
                  placeholder="O que vai aparecer na caixa de entrada"
                />
                <Input
                  value={v.preheader}
                  onChange={(e) => updateVariant(idx, { preheader: e.target.value })}
                  className="h-7 text-xs mt-1"
                  placeholder="Preheader (opcional)"
                />
                {variantStats && (
                  <div className="text-[10px] text-muted-foreground mt-1 flex gap-3">
                    <span>Enviados: {variantStats.sentCount ?? 0}</span>
                    <span>Abriram: {variantStats.openCount ?? 0}</span>
                    <span>Clicaram: {variantStats.clickCount ?? 0}</span>
                    {isWinner && <span className="text-emerald-700 font-medium">★ vencedor</span>}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeVariant(idx)}
                disabled={variants.length <= 2}
                title="Remover variante"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={addVariant}
        disabled={variants.length >= 4}
        className="text-xs gap-1.5 mb-3"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar variante
      </Button>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <Label className="text-[10px] text-muted-foreground">Critério</Label>
          <Select value={criteria} onValueChange={(v) => setCriteria(v as Criteria)}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open_rate" className="text-xs">Taxa de abertura</SelectItem>
              <SelectItem value="click_rate" className="text-xs">Taxa de clique</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Duração (h)</Label>
          <Input
            type="number"
            min={1}
            max={72}
            value={durationHours}
            onChange={(e) => setDurationHours(Number(e.target.value))}
            className="h-7 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Amostra (%)</Label>
          <Input
            type="number"
            min={5}
            max={50}
            value={samplePercent}
            onChange={(e) => setSamplePercent(Number(e.target.value))}
            className="h-7 text-xs"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={setupMutation.isPending || !canSave}
          className="flex-1 text-xs"
        >
          {setupMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
          Salvar configuração
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => pickWinnerMutation.mutate({ campaignId, criteria })}
          disabled={pickWinnerMutation.isPending || !existing || existing.length === 0}
          className="text-xs gap-1.5"
          title="Calcula vencedor com base nos contadores atuais"
        >
          {pickWinnerMutation.isPending
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Trophy className="w-3.5 h-3.5" />}
          Escolher vencedor agora
        </Button>
      </div>
    </Card>
  );
}
